import {
  Divider,
  TextField,
  FormControlLabel,
  Stack,
  Checkbox,
  Grid,
  Card,
} from '@mui/material';

import Autocomplete from '@mui/material/Autocomplete';
import { useImmer } from 'use-immer';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectInvoice,
  doGetProformaAsync,
  updateInvoice,
  doGetNextSequenceValueAsync,
  doSetupNewProformaAsync,
  doGetProformaBasedOnProformaAsync,
  selectInvoiceValidation,
  setInvoiceValidation,
  updateSeller,
  updateBuyer,
  updateBuyerUserId,
  selectIsLoading,
  setIsLoading,
} from './ProformaEditorSlice';
import { AddOrUpdateContractor, AddOrUpdateProduct, PutOrganization, PutProforma } from '../../engine/baseApiClient';
import { getInvoiceSeriesCodes } from '../../utility/documentSeries';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { createPdfExportObject, downloadPdf } from '../../engine/documentExporter';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/pl';
import { getLabelForPeriod, getPeriods } from '../../utility/invoicePeriods';
import { doFetchOrganizationDataAsync, selectOrganizationSettings } from '../user/userSlice';
import UserActionWithAuthPopupButton from '../../components/userActionWithAuthPopupButton';
import navigationPaths from '../../engine/navigationPaths';
import ContractorPanel from '../../components/contractor/contractorPanel';
import {
  getLabelForPaymentMethod,
  getLabelForPaymentMethodDeadlines,
  getPaymentMethods,
  getPaymentMethodsDeadlines,
} from '../../utility/paymentMethods';
import { selectRoles } from '../logon/logonSlice';
import { doSetTabs } from '../toolbarNavigationMenu/toolbarNavigationMenuSlice';
import ToolbarNavigationMenuIds from '../toolbarNavigationMenu/toolbarNavigationMenuIds';
import TooltipButton from '../../components/tooltipButton';
import ToastMessage from '../../components/toastMessage';
import ItemsPanel from './ItemsPanel';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import BillzTypographyTitle from 'components/BillzTypographies/BillzTypographyTitle';
import BillzTypography from 'components/BillzTypographies/BillzTypography';
import BillzInput from 'components/BillzTypographies/BillzInput';
import { OEM } from 'utility/OEMProvider';

export default function ProformaEditor() {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const invoice = useSelector(selectInvoice);
  const organization = useSelector(selectOrganizationSettings);
  const invoiceValidation = useSelector(selectInvoiceValidation);
  const userRoles = useSelector(selectRoles);
  const [loading, setLoading] = React.useState(true);
  const selectedIsLoading = useSelector(selectIsLoading);

  // general isReadOnly state for the form, might be not used in some cases, for eg. number handling
  const [isReadOnly, updateIsReadOnly] = useImmer(false);

  const [isReadOnlyOverrides, updateIsReadOnlyOverrides] = useImmer({
    seriesPrefix: false,
  });

  React.useEffect(() => {
    dispatch(
      doSetTabs({
        mainTabId: ToolbarNavigationMenuIds().RootDocumentsTab,
        subTabId: ToolbarNavigationMenuIds().DocumentsAddInvoiceSubTab,
      }),
    );

    dispatch(doFetchOrganizationDataAsync());

    if (location.state.mode === 'create') {
      dispatch(doSetupNewProformaAsync());
      updateIsReadOnly((draft) => false);
      updateIsReadOnlyOverrides((draft) => {
        draft.seriesPrefix = false;
      });
    } else if (location.state.mode === 'edit') {
      dispatch(doGetProformaAsync(location.state.id));
      updateIsReadOnly((draft) => false);
      updateIsReadOnlyOverrides((draft) => {
        draft.seriesPrefix = true;
      });
    } else if (location.state.mode === 'editPreview') {
      dispatch(
        doGetProformaBasedOnProformaAsync({
          id: location.state.id,
          invoiceDateUtc: dayjs(),
          timezone: dayjs.tz.guess(),
        }),
      );
      updateIsReadOnly((draft) => false);
      updateIsReadOnlyOverrides((draft) => {
        draft.seriesPrefix = false;
      });
    }

    return () => {
      dispatch(setIsLoading(true));
    };
  }, []);

  React.useEffect(() => {
    setTimeout(() => {
      setLoading(selectedIsLoading);
    }, 200);
  }, [selectedIsLoading]);

  React.useEffect(() => {
    dispatch(updateInvoice(buildPayload('placeOfIssue', organization.company.address.city)));
  }, [organization]);

  const isError = (obj) => {
    return obj === false;
  };

  const isInvalid = (obj) => {
    return obj === false || obj === undefined;
  };

  const validateObj = (obj, invalidProps) => {
    var isValid = true;

    for (var prop in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, prop)) {
        if (obj[prop] !== undefined && Object.keys(obj[prop]).length) {
          if (typeof obj[prop] === 'string') {
            invalidProps[prop] = obj[prop];
          } else {
            invalidProps[prop] = Array.isArray(obj[prop]) ? [] : {};
            isValid = isValid & validateObj(obj[prop], invalidProps[prop]);
          }

          continue;
        }
        if (isInvalid(obj[prop])) {
          console.log(prop);
          invalidProps[prop] = false;
          isValid = false;
        } else {
          invalidProps[prop] = true;
        }
      }
    }
    return isValid;
  };

  const validate = () => {
    var isValid = true;
    var invalidProps = {};
    isValid = validateObj(invoiceValidation, invalidProps);
    console.log(invalidProps);
    dispatch(setInvoiceValidation(invalidProps));
    console.log(isValid);
    return isValid;
  };

  const createInvoiceObject = () => {
    const clone = JSON.parse(JSON.stringify(invoice));
    return clone;
  };

  const createBuyerObject = () => {
    const clone = JSON.parse(JSON.stringify(invoice.buyer));
    return clone;
  };

  const toProductPayload = (data) => {
    return {
      name: data.name,
      pkWiU: data.pkwiu,
      unit: data.unit,
      unitNetPrice: data.unitNetPrice,
      type: 'product'
    };
  }

  const createInvoiceEvent = async (e) => {
    if (!validate()) {
      return;
    }
    var invoiceObject = await createInvoiceObject();
    if (enabledWriteContractor) {
      const res = await AddOrUpdateContractor(await createBuyerObject());
      dispatch(updateBuyerUserId(res.guid));
      dispatch(
        doGetNextSequenceValueAsync(invoice.seriesPrefix + '/' + invoice.dateOfIssueUtc.format('YYYY') + '/PROFORMA'),
      );
      invoiceObject.buyer.userId = res.guid;
    }

    // if the tax number is empty it means that we do not have any organization present
    if (organization.company.nip.nip === '') {
      const payload = {
        company: invoice.seller,
        bankAccounts: organization.bankAccounts,
        automaticSendingOfDocuments: organization.automaticSendingOfDocuments,
      };

      await PutOrganization(payload);
      dispatch(doFetchOrganizationDataAsync());
    }

    if(OEM().isInventoryEnabled) {
      for (const item of invoiceObject.items) {
        if(item.productId === '') {
          const productId = await AddOrUpdateProduct(toProductPayload(item));
          item.productId = productId.guid;
        }
      };
    };

    var response = await PutProforma(invoiceObject);
    if (response.status == 500) {
      setOpenInvoiceErrorToast(true);
      setInvoiceErrorMessage(response.detail);
      return;
    }
    navigate(navigationPaths().Proformas);
  };

  const createAndSavePdf = async (e) => {
    if (!validate()) {
      return;
    }
    var invoiceObject = await createInvoiceObject();
    if (enabledWriteContractor) {
      const res = await AddOrUpdateContractor(await createBuyerObject());
      dispatch(updateBuyerUserId(res.guid));
      dispatch(
        doGetNextSequenceValueAsync(invoice.seriesPrefix + '/' + invoice.dateOfIssueUtc.format('YYYY') + '/PROFORMA'),
      );
      invoiceObject.buyer.userId = res.guid;
    }

    // if the tax number is empty it means that we do not have any organization present
    if (organization.company.nip.nip === '') {
      const payload = {
        company: invoice.seller,
        bankAccounts: organization.bankAccounts,
        automaticSendingOfDocuments: organization.automaticSendingOfDocuments,
      };

      await PutOrganization(payload);
      dispatch(doFetchOrganizationDataAsync());
    }

    var documentId = await PutProforma(invoiceObject);
    if (documentId.status == 500) {
      setOpenInvoiceErrorToast(true);
      setInvoiceErrorMessage(documentId.detail);
      return;
    }
    var payload = createPdfExportObject('proforma');
    payload.document = await createInvoiceObject();
    payload.documentId = documentId;
    await downloadPdf(payload);

    dispatch(doGetProformaAsync(documentId));
    updateIsReadOnly((draft) => false);
    updateIsReadOnlyOverrides((draft) => {
      draft.seriesPrefix = true;
    });
    updateIsReadOnly((draft) => false);
    updateIsReadOnlyOverrides((draft) => {
      draft.seriesPrefix = false;
    });
  };

  const exportInvoiceEvent = async (e) => {
    await createAndSavePdf(e);
  };

  const onCancelEditorEventHandler = async (e) => {
    dispatch(doSetupNewProformaAsync());
    navigate(navigationPaths().Proformas);
  };

  function getVariantType() {
    return isReadOnly ? 'filled' : 'outlined';
  }

  // #region Write Contractor state

  const [enabledWriteContractor, setEnabledWriteContractor] = React.useState(true);

  // #endregion

  const SendEmailField = () => {
    return (
      <Stack sx={{ mt: 1, flex: true }} justifyContent="space-between">
        <FormControlLabel
          control={
            <Checkbox
              id="seller-send-email-check"
              size="small"
              onChange={(e) => onSendEmailChecked(e)}
              checked={invoice.settings.sendEmail}
            />
          }
          label={<BillzTypography>Wyślij dokument na adres email</BillzTypography>}
          sx={{}}
        />
        <BillzInput
          label="Adres e-mail"
          variant={getVariantType()}
          sx={{ mt: 1 }}
          disabled={isReadOnly || !invoice.settings.sendEmail}
          InputProps={{ sx: { flex: true } }}
          value={invoice.settings.emailToSend}
          onChange={(e) => onSendEmailFieldChanged(e)}
        />
      </Stack>
    );
  };

  const GeneratePeriodicInvoicefield = () => {
    return (
      <Stack direction="row">
        <FormControlLabel
          control={
            <Checkbox
              id="seller-periodic-invoice-check"
              size="small"
              onChange={(e) => onPeriodicInvoiceChecked(e)}
              checked={invoice.settings.isPeriodicInvoice}
            />
          }
          label={<BillzTypography>Faktura okresowa</BillzTypography>}
          sx={{ width: 180 }}
        />
        <Autocomplete
          id="invoice-editor-periodic-invoice-period"
          size="small"
          disableClearable
          disabled={isReadOnly || !invoice.settings.isPeriodicInvoice}
          sx={{ width: 150 }}
          value={invoice.settings.issuingPeriod}
          onChange={(e, newValue) => onPeriodicInvoiceFieldChanged(e, newValue)}
          isOptionEqualToValue={onIsOptionEqualToId}
          getOptionLabel={getPeriodOptionLabel}
          options={getPeriods().map((option) => ({ id: option.value, label: option.label }))}
          noOptionsText="brak"
          renderInput={(params) => <BillzInput {...params} label="Okres" />}
        />
      </Stack>
    );
  };

  const onIsOptionEqualToValue = (option, value) => {
    return option.label === value;
  };

  const onIsOptionEqualToValue2 = (option, value) => {
    return option.label === value;
  };

  const onIsOptionEqualToId = (option, value) => {
    return option.id === value;
  };

  const getPeriodOptionLabel = (option) => {
    const id = typeof option === 'string' ? option : option.id;
    return getLabelForPeriod(id);
  };

  const getPaymentOptionLabel = (option) => {
    const id = typeof option === 'string' ? option : option.id;
    return getLabelForPaymentMethod(id);
  };

  const getPaymentDeadlineOptionLabel = (option) => {
    const id = typeof option === 'number' ? option : option.id;
    return getLabelForPaymentMethodDeadlines(id);
  };

  // #region state onChange handlers

  const buildPayload = (id, value, param) => {
    return {
      type: id,
      value: value,
      param: param,
    };
  };

  const onSellerDataChanged = (event, sellerData) => {
    dispatch(updateInvoice(buildPayload('placeOfIssue', sellerData.address.city)));
    dispatch(updateSeller(sellerData));
  };

  const onBuyerDataChanged = (event, buyerData, buyerId) => {
    dispatch(updateBuyer({ data: buyerData, buyerId: buyerId }));
  };

  const onPaymentAccountNumberChanged = (event, newValue) => {
    dispatch(updateInvoice(buildPayload('paymentBankAccount', newValue)));
  };

  const onCurrencyChanged = (event, value) => {
    dispatch(updateInvoice(buildPayload('currency.code', value.label)));
  };

  const onPaymentMethodChanged = (event, value) => {
    dispatch(updateInvoice(buildPayload('payment.code', value.id)));
  };

  const onPaymentDeadlineChanged = (event, value) => {
    dispatch(updateInvoice(buildPayload('payment.deadline', value.id)));
  };

  const onSeriesChanged = (event, value) => {
    dispatch(updateInvoice(buildPayload('series', value + '/' + invoice.dateOfIssueUtc.format('YYYY') + '/PROFORMA')));
    dispatch(updateInvoice(buildPayload('seriesPrefix', value)));
    dispatch(doGetNextSequenceValueAsync(value + '/' + invoice.dateOfIssueUtc.format('YYYY') + '/PROFORMA'));
  };

  const onDateOfIssueUtcChanged = (value) => {
    if (value != null) {
      dispatch(updateInvoice(buildPayload('dateOfIssueUtc', value)));
      dispatch(updateInvoice(buildPayload('dateOfSaleUtc', value)));
      dispatch(updateInvoice(buildPayload('series', invoice.seriesPrefix + '/' + value.format('YYYY') + '/PROFORMA')));
      dispatch(doGetNextSequenceValueAsync(invoice.seriesPrefix + '/' + value.format('YYYY') + '/PROFORMA'));
    }
  };

  const onDateOfSaleUtcChanged = (value) => {
    if (value != null) dispatch(updateInvoice(buildPayload('dateOfSaleUtc', value)));
  };

  const onSendEmailChecked = (event) => {
    dispatch(updateInvoice(buildPayload('settings.sendEmail', event.target.checked)));
  };
  const onPeriodicInvoiceChecked = (event) => {
    dispatch(updateInvoice(buildPayload('settings.isPeriodicInvoice', event.target.checked)));
  };

  const onSendEmailFieldChanged = (event) => {
    dispatch(updateInvoice(buildPayload('settings.emailToSend', event.target.value)));
  };
  const onPeriodicInvoiceFieldChanged = (event, newValue) => {
    dispatch(updateInvoice(buildPayload('settings.issuingPeriod', newValue.id)));
  };

  const [openInvoiceErrorToast, setOpenInvoiceErrorToast] = React.useState(false);
  const [invoiceErrorMessage, setInvoiceErrorMessage] = React.useState('');

  const [editNumber, setEditNumber] = React.useState(false);

  const toggleEditNumber = (event) => {
    setEditNumber(!editNumber);
  };

  const overwriteInvoiceNumber = (e) => {
    const regex = /^\d+$/;
    if (regex.test(e.target.value) || e.target.value == '') {
      dispatch(updateInvoice(buildPayload('number', e.target.value)));
    }
  };
  // #endregion

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
      <DashboardLayout isLoading={loading}>
        <DashboardNavbar />
        <MDBox id="invoice-container" py={1} preloading>
          <ToastMessage
            open={openInvoiceErrorToast}
            onClose={() => setOpenInvoiceErrorToast(false)}
            severity="error"
            message={invoiceErrorMessage}
          />
          <Grid container justifyContent="center" alignItems="flex-start" columns={8} columnSpacing={14} rowSpacing={1}>
            <Grid item xs={12}>
              <Card sx={{ display: 'flex', px: 3, pt: 2, pb: 3 }}>
                <MDBox mb={3}>
                  <BillzTypographyTitle>Nowa faktura Pro-Forma</BillzTypographyTitle>
                </MDBox>

                <Stack
                  direction={{ xs: 'column', xl: 'row' }}
                  sx={{ pt: 2 }}
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap={3}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Autocomplete
                      id="invoice-editor-prefix-select"
                      size="small"
                      disableClearable
                      disabled={isReadOnlyOverrides.seriesPrefix}
                      value={invoice.seriesPrefix}
                      onChange={(e, newValue) => onSeriesChanged(e, newValue)}
                      options={getInvoiceSeriesCodes()}
                      noOptionsText="brak"
                      renderInput={(params) => <MDInput {...params} required label="Prefix" />}
                    />
                    {editNumber ? (
                      <Stack alignItems={'center'} spacing={2} mt={2} direction="row">
                        <TextField
                          value={invoice.numberPromise}
                          onChange={overwriteInvoiceNumber}
                          sx={{ width: 100 }}
                          size="small"
                          id="invoice-number"
                        ></TextField>
                        <MDTypography
                          component="label"
                          variant="h6"
                          fontWeight="regular"
                          color="text"
                          textTransform="capitalize"
                          spacing={2}
                          mt={2}
                          value={invoice.dateOfIssueUtc}
                        >
                          /{invoice.series}
                        </MDTypography>
                      </Stack>
                    ) : (
                      <MDTypography
                        id="expected-invoice-number"
                        component="label"
                        variant="h6"
                        fontWeight="regular"
                        color="text"
                        textTransform="capitalize"
                        spacing={2}
                        mt={2}
                        value={invoice.dateOfIssueUtc}
                      >
                        Przewidywany numer: {invoice.numberPromise}/{invoice.series}
                      </MDTypography>
                    )}
                    {editNumber ? (
                      <SaveIcon
                        id="save-number-button"
                        color="secondary"
                        sx={{ cursor: 'pointer', width: '15' }}
                        onClick={toggleEditNumber}
                        size="small"
                      />
                    ) : (
                      <EditIcon
                        id="edit-number-button"
                        color="secondary"
                        sx={{ cursor: 'pointer', width: '15' }}
                        onClick={toggleEditNumber}
                        size="small"
                      />
                    )}
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', md: 'row', xl: 'column', xxl: 'row' }}
                    spacing={2}
                    alignItems="flex-end"
                  >
                    <div>{GeneratePeriodicInvoicefield()}</div>
                    <Stack direction="row" spacing={2}>
                      <DesktopDatePicker
                        label="Data wystawienia"
                        readOnly={isReadOnly}
                        InputProps={{ sx: { width: 150 } }}
                        error={isError(invoiceValidation.dateOfIssueUtc)}
                        value={invoice.dateOfIssueUtc}
                        onChange={onDateOfIssueUtcChanged}
                        renderInput={(params) => (
                          <TextField
                            id="invoice-date-of-issue"
                            variant={getVariantType()}
                            disabled={isReadOnly}
                            required
                            size="small"
                            {...params}
                          />
                        )}
                      />
                      <DesktopDatePicker
                        label="Data sprzedaży"
                        readOnly={isReadOnly}
                        InputProps={{ sx: { width: 150 } }}
                        error={isError(invoiceValidation.dateOfSaleUtc)}
                        value={invoice.dateOfSaleUtc}
                        onChange={onDateOfSaleUtcChanged}
                        renderInput={(params) => (
                          <TextField
                            id="invoice-date-of-sale"
                            variant={getVariantType()}
                            disabled={isReadOnly}
                            required
                            size="small"
                            {...params}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Divider />

                <Stack direction={{ xs: 'column', xl: 'row' }} justifyContent="space-between" gap={3}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    gap={3}
                    sx={{ flex: 1 }}
                  >
                    <ContractorPanel
                      id="sellerpanel"
                      title="Twoje dane"
                      data={invoice.seller}
                      onContractorChanged={onSellerDataChanged}
                      dataValidation={invoiceValidation.seller.nip.nip}
                    >
                      <Autocomplete
                        size="small"
                        id="payments-bank-account"
                        sx={{ width: '100%' }}
                        filterOptions={(x) => x}
                        options={organization.bankAccounts}
                        renderInput={(params) => <MDInput {...params} label="Konto bankowe" />}
                        value={invoice.paymentBankAccount}
                        autoComplete
                        filterSelectedOptions
                        noOptionsText="Brak zdefiniowanych kont bankowych"
                        getOptionLabel={(option) =>
                          option === undefined ? option : option.name + ' (' + option.accountNumber + ')'
                        }
                        onChange={(event, newValue) => {
                          onPaymentAccountNumberChanged(event, newValue);
                        }}
                      />
                    </ContractorPanel>

                    <ContractorPanel
                      id="buyerpanel"
                      title="Dane klienta"
                      data={invoice.buyer}
                      dataValidation={invoiceValidation.buyer.nip.nip}
                      onContractorChanged={onBuyerDataChanged}
                      searchEnabled={true}
                      searchHelpText="Pobierz z GUS lub wyszukaj po nazwie, mieście, email lub telefonie"
                      searchFieldLabel="NIP"
                      enableAddModeWhenNotFilled={true}
                    />
                  </Stack>

                  <Stack>
                    <BillzTypographyTitle>Płatność</BillzTypographyTitle>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Autocomplete
                        disablePortal
                        size="small"
                        sx={{ width: 180 }}
                        disableClearable
                        variant={getVariantType()}
                        disabled={isReadOnly}
                        value={invoice.payment.code}
                        isOptionEqualToValue={onIsOptionEqualToId}
                        getOptionLabel={getPaymentOptionLabel}
                        onChange={(event, value) => onPaymentMethodChanged(event, value)}
                        options={getPaymentMethods().map((option) => ({ id: option.value, label: option.label }))}
                        renderInput={(params) => (
                          <MDInput
                            variant={getVariantType()}
                            disabled={isReadOnly}
                            {...params}
                            label="Sposób płatności"
                          />
                        )}
                      />
                      <Autocomplete
                        disablePortal
                        size="small"
                        sx={{ width: 110 }}
                        disableClearable
                        variant={getVariantType()}
                        disabled={isReadOnly}
                        value={invoice.payment.deadline}
                        isOptionEqualToValue={onIsOptionEqualToValue2}
                        getOptionLabel={getPaymentDeadlineOptionLabel}
                        onChange={(event, value) => onPaymentDeadlineChanged(event, value)}
                        options={getPaymentMethodsDeadlines().map((option) => ({
                          id: option.value,
                          label: option.label,
                        }))}
                        renderInput={(params) => (
                          <MDInput variant={getVariantType()} disabled={isReadOnly} {...params} label="Termin" />
                        )}
                      />
                    </Stack>
                    <Stack sx={{ flex: true }} direction="row" justifyContent="space-between" marginBottom={1}>
                      {SendEmailField()}
                    </Stack>
                  </Stack>
                </Stack>

                <Divider sx={{ mb: 4 }} />

                <ItemsPanel isReadOnly={isReadOnly} />
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ overflow: 'visible', px: 3, py: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ my: 1 }}>
                  <MDButton
                    id="invoice-close-button"
                    variant="outlined"
                    color="secondary"
                    onClick={onCancelEditorEventHandler}
                  >
                    Anuluj
                  </MDButton>

                  <Stack direction="row">
                    <UserActionWithAuthPopupButton
                      id="export-invoice-as-pdf"
                      variant="outlined"
                      color="secondary"
                      sx={{ mr: 2 }}
                      onClick={exportInvoiceEvent}
                      onPreflightValidation={validate}
                      onSuccess={exportInvoiceEvent}
                    >
                      Pobierz PDF
                    </UserActionWithAuthPopupButton>

                    <UserActionWithAuthPopupButton
                      id="saveInvoice"
                      variant="contained"
                      color="info"
                      disabled={isReadOnly}
                      onClick={createInvoiceEvent}
                      onPreflightValidation={validate}
                      onSuccess={createInvoiceEvent}
                    >
                      Zapisz
                    </UserActionWithAuthPopupButton>

                    <TooltipButton
                      id="invoice-save-tooltip"
                      iconSx={{ width: 20, height: 20 }}
                      title="Zapis faktury"
                      message="Zapis faktury nie powoduje skutków księgowych. Faktury nie są automatycznie wysyłane do Urzędu Skarbowego i Krajowego Systemu e-Faktur (KSEF)."
                    />
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </DashboardLayout>
    </LocalizationProvider>
  );
}
